import { EditorView } from "prosemirror-view";
import { getResult } from "../../core/helpers/functional-util.js";
import Model from "../Model.js";
import WysiwygStateManager from "./WysiwygStateManager.js";

/**
 * Rich text editor using ProseMirror. If the parent node element is
 * not ready yet on initialization, an `update()` will be needed when
 * the DOM element is finally attached.
 *
 * @param {string|function} content - Initial content of the editor, or a function to fetch it
 * @param {function} [onChange] - Called when the editor has changed. It
 *   takes the editor AST content as argument
 * @param {HTMLElement | function} parentNode - Must contain or
 *   return the DOM element that will contain the editor
 * @param {AbstractAction[]} [actions] - List of actions to append to the editor
 * @param {boolean} [forceHTML] - When true, the editor output changes as HTML,
 *   regardless of the mode (AST/HTML)
 * @param {function} [dropdownMenuItemBuilder] - Takes the dropdown
 *   options as argument (it must contain an `items` and a `label`
 *   fields). It should return an instance of a dropdown compatible
 *   with ProseMirror menu items. Every dropdown of ProseMirror's
 *   MenuBar will use the same builder. Defaults to
 *   `defaultDropdownItemBuilder` but can be used with
 *   `wrapAsProseMirrorDropdown` to use one of our own widgets
 */
export default class WysiwygEditor extends Model {
	constructor({
		content,
		onChange = () => {},
		parentNode,
		actions = [],
		forceHTML = false,
		dropdownMenuItemBuilder,
	} = {}) {
		super(...arguments);

		this._parentNode = parentNode;
		this._prosemirror = null;

		this._stateManager = new WysiwygStateManager({
			content,
			actions,
			onChange,
			forceHTML,
			dropdownMenuItemBuilder,
		});

		this.update();
	}

	focus() {
		this._prosemirror?.focus();
	}

	tearDown() {
		this._prosemirror = undefined;
	}

	update() {
		const parentNode = getResult(this._parentNode);
		if (!parentNode) {
			return;
		}

		this._ensureProseMirrorEditorView({ parentNode });
	}

	updateContent() {
		const updateContentTransaction =
			this._stateManager.getUpdateContentTransaction();
		if (!updateContentTransaction) {
			return false;
		}

		return updateContentTransaction(
			this._prosemirror.state,
			this._prosemirror.dispatch,
		);
	}

	replaceWholeTextWithHTML(text) {
		return this._stateManager.replaceWholeTextWithHTML(text)(
			this._prosemirror.state,
			this._prosemirror.dispatch,
		);
	}

	replaceWholeTextWithASTNode(astNode) {
		return this._stateManager.replaceWholeTextWithASTNode(astNode)(
			this._prosemirror.state,
			this._prosemirror.dispatch,
		);
	}

	replaceSelectionWithHTML(text) {
		return this._stateManager.replaceSelectionWithHTML(text)(
			this._prosemirror.state,
			this._prosemirror.dispatch,
		);
	}

	replaceSelectionWithASTNode(node) {
		return this._stateManager.replaceSelectionWithASTNode(node)(
			this._prosemirror.state,
			this._prosemirror.dispatch,
		);
	}

	_ensureProseMirrorEditorView({ parentNode }) {
		if (this._prosemirror) {
			return;
		}
		const { state, dispatchTransaction, nodeViews } =
			this._stateManager.getEditorViewSpec();

		this._prosemirror = new EditorView(parentNode, {
			state,
			dispatchTransaction,
			nodeViews,
		});
	}
}
